import type { ReactElement } from 'react'
import styles from './SidePanelRouteLoading.module.css'

const VARIANT_COUNT = 3
const LEG_COUNT = 2

/**
 * Loading state of the tablet / desktop side panel. The panel's results
 * are a vertical column of 388 × 74 variant cards with the selected
 * route's details inline below (TASK-412), so loading stands in for both
 * at the same geometry: three variant-card skeletons, then the details
 * skeleton — the "Entry" section header, the walk-to-platform row and two
 * legs on the timeline rail. The phone keeps {@link RouteLoading}'s
 * horizontal strip (owner 2026-10-20: the strip read as the mobile
 * layout on desktop).
 *
 * Same bar language and shimmer as {@link SkeletonRouteCard}: hairline
 * cards, `bg/secondary` bars, an alpha pulse frozen under
 * `prefers-reduced-motion`.
 */
export function SidePanelRouteLoading(): ReactElement {
  return (
    <div
      className={styles.root}
      data-testid="route-loading-side-panel"
      aria-busy="true"
      aria-live="polite"
    >
      <ul className={styles.variants}>
        {Array.from({ length: VARIANT_COUNT }, (_, idx) => (
          <li key={idx} className={styles.variant} data-testid="route-loading-variant-skeleton">
            <span className={styles.variantTop}>
              <span className={`${styles.bar} ${styles.title}`} />
              <span className={`${styles.bar} ${styles.subtitle}`} />
            </span>
            <span className={styles.badges}>
              <span className={`${styles.bar} ${styles.badge}`} />
              <span className={`${styles.bar} ${styles.badge}`} />
            </span>
          </li>
        ))}
      </ul>
      <div
        className={styles.details}
        data-testid="route-loading-details-skeleton"
        aria-hidden="true"
      >
        <span className={styles.sectionHeader}>
          <span className={`${styles.bar} ${styles.sectionTitle}`} />
        </span>
        <span className={styles.entryRow}>
          <span className={`${styles.bar} ${styles.entryIcon}`} />
          <span className={`${styles.bar} ${styles.entryLabel}`} />
        </span>
        {Array.from({ length: LEG_COUNT }, (_, idx) => (
          <span key={idx} className={styles.leg} data-testid="route-loading-leg-skeleton">
            <span className={`${styles.bar} ${styles.time}`} />
            <span className={styles.rail}>
              <span className={`${styles.bar} ${styles.connector}`} />
              <span className={`${styles.bar} ${styles.marker}`} />
            </span>
            <span className={styles.legContent}>
              <span className={`${styles.bar} ${styles.stopTitle}`} />
              <span className={`${styles.bar} ${styles.lineSubtitle}`} />
              <span className={`${styles.bar} ${styles.chip}`} />
              <span className={`${styles.bar} ${styles.towards}`} />
            </span>
          </span>
        ))}
      </div>
    </div>
  )
}
